import * as React from 'react';
import { cn } from '@shared/lib/utils';

export interface PageHeaderProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Breadcrumb element rendered above the title. */
  breadcrumb?: React.ReactNode;
  /** Right-aligned actions (buttons, menus). */
  actions?: React.ReactNode;
  /**
   * Tighter vertical rhythm and a smaller title, for dense working screens
   * where the header competes with the content for the fold.
   *
   * Opt-in, so nothing that already uses this component changes. It is a
   * density switch and nothing else — the same elements, the same order, the
   * same semantics; only the spacing and the title size differ.
   */
  compact?: boolean;
  className?: string;
  children?: React.ReactNode;
}

/**
 * Standard page header: optional breadcrumb, a title with optional description,
 * and a right-aligned actions area that wraps gracefully on narrow viewports.
 */
export function PageHeader({
  title,
  description,
  breadcrumb,
  actions,
  compact = false,
  className,
  children,
}: PageHeaderProps) {
  return (
    <header className={cn('flex flex-col', compact ? 'gap-2' : 'gap-4', className)}>
      {breadcrumb ? <div>{breadcrumb}</div> : null}
      <div
        className={cn(
          'flex flex-col justify-between sm:flex-row sm:items-start',
          compact ? 'gap-2' : 'gap-4',
        )}
      >
        <div className={cn('flex flex-col', compact ? 'gap-0.5' : 'gap-1')}>
          <h1
            className={cn(
              'font-semibold tracking-tight text-foreground',
              compact ? 'text-xl' : 'text-2xl',
            )}
          >
            {title}
          </h1>
          {description ? (
            <p
              className={cn(
                'max-w-2xl text-muted-foreground',
                compact ? 'text-compact' : 'text-sm',
              )}
            >
              {description}
            </p>
          ) : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
      {children}
    </header>
  );
}
